import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { PATIENT_STATUS, PatientConsultationStatus } from "~/common/constants";
import { useHandleMultipleFilters } from "~/common/hooks";
import { Group } from "~/common/utils";
import {
  hasConsultationAccess,
  hasGeneralAccess,
} from "~/common/utils/checkUserPermissions";
import { PATIENT_FILTER_PARAMS } from "../constants";
import {
  ENGAGEMENT_OPTIONS,
  engagementFilterOptions,
  EngagementTypes,
  PATIENT_CONSULTATION_STATUS_BASIC_OPTIONS,
} from "../constants/patientSearchConstants";
import { useGetCurrentTeamMember } from "./queries";

export const NONE = "none" as const;

export const useHandlePatientSearchFilters = () => {
  const [params, setParams] = useSearchParams();
  const paramName = params.get(PATIENT_FILTER_PARAMS.name) ?? undefined;
  const [nameFilter, setNameFilter] = useState<string | undefined>(paramName);
  const { handleMultipleFilters } = useHandleMultipleFilters();
  const { data: teamMember } = useGetCurrentTeamMember();

  const userPermissions = teamMember?.user_permissions ?? [];
  const handlePatientSearchFilters = (filters: string[]) => {
    const engagementFiltersValues = filters.filter((filter) =>
      ENGAGEMENT_OPTIONS.includes(filter as EngagementTypes),
    );

    const engagementFilters = engagementFiltersValues.length
      ? engagementFiltersValues
      : [NONE];

    const consultationStatusFilters = filters.filter((filter) =>
      PATIENT_CONSULTATION_STATUS_BASIC_OPTIONS.includes(
        filter as PatientConsultationStatus,
      ),
    );

    handleMultipleFilters(PATIENT_FILTER_PARAMS.engagements, engagementFilters);

    handleMultipleFilters(
      PATIENT_FILTER_PARAMS.consultationStatus,
      consultationStatusFilters,
    );
  };

  const currentEngagementFilters = params.getAll(
    PATIENT_FILTER_PARAMS.engagements,
  );

  const currentConsultationStatusFilters = params.getAll(
    PATIENT_FILTER_PARAMS.consultationStatus,
  );

  const currentFilters = [
    ...currentEngagementFilters,
    ...currentConsultationStatusFilters,
  ];

  const multiSelectGroupValues = [
    ...currentEngagementFilters,
    ...currentConsultationStatusFilters,
  ];

  const handleNameFilterChange = (value?: string) => {
    setNameFilter(value);

    const newParams = new URLSearchParams(params);
    if (value) {
      newParams.set(PATIENT_FILTER_PARAMS.name, value);
      newParams.delete(PATIENT_FILTER_PARAMS.page);
    } else {
      newParams.delete(PATIENT_FILTER_PARAMS.name);
    }
    setParams(newParams);
  };

  const patientSearchGroupOptions =
    hasConsultationAccess(userPermissions) && !hasGeneralAccess(userPermissions)
      ? engagementFilterOptions.filter(
          (option) => option.value !== PATIENT_STATUS.active,
        )
      : engagementFilterOptions;

  const patientSearchFiltersOptions: Group[] = [
    {
      group: "Engagement",
      groupOptions: patientSearchGroupOptions,
    },
  ] as const;

  const hasAllFiltersApplied =
    currentEngagementFilters.length === patientSearchGroupOptions.length;

  return {
    handlePatientSearchFilters,
    multiSelectGroupValues,
    handleNameFilterChange,
    nameFilter,
    currentFilters,
    patientSearchFiltersOptions,
    hasAllFiltersApplied,
  };
};
